import type { ConversationAudio, ConversationDraft } from "@/types/conversation";

export class ConversationSession {
	private sendOperation: { id: string; revision: number } | null = null;

	private listeners = new Set<() => void>();
	active = true;

	state: ConversationDraft = {
		text: "",
		direction: "",
		suggestion: null,
		directed: false,
		revision: 0,
		suggesting: false,
		suggestionError: false,
		sending: false,
		reactivating: false,
		sendError: null,
		sendResult: null,
		images: [],
		audio: null,
	};

	reading = { top: 0, atBottom: true, lastId: undefined as string | undefined };

	subscribe = (listener: () => void) => {
		this.listeners.add(listener);

		return () => this.listeners.delete(listener);
	};

	snapshot = () => this.state;

	patch(fields: Partial<ConversationDraft>) {
		if (!this.active) {
			return;
		}

		this.state = { ...this.state, ...fields };

		for (const listener of this.listeners) {
			listener();
		}
	}

	editText(text: string) {
		this.patch({ text, revision: this.state.revision + 1 });
	}

	editDirection(direction: string) {
		this.patch({ direction, revision: this.state.revision + 1 });
	}

	addImages(files: File[]) {
		const images = files.map((file) => ({
			id: crypto.randomUUID(),
			file,
			url: URL.createObjectURL(file),
		}));

		this.patch({ images: [...this.state.images, ...images] });
	}

	removeImage(id: string) {
		const image = this.state.images.find((item) => item.id === id);

		if (image) {
			URL.revokeObjectURL(image.url);
		}

		this.patch({
			images: this.state.images.filter((item) => item.id !== id),
		});
	}

	setAudio(audio: ConversationAudio) {
		if (!this.active) {
			URL.revokeObjectURL(audio.url);

			return;
		}

		this.patch({ audio });
	}

	removeAudio() {
		if (this.state.audio) {
			URL.revokeObjectURL(this.state.audio.url);
		}

		this.patch({
			audio: null,
		});
	}

	sendIntent() {
		if (!this.sendOperation || this.sendOperation.revision !== this.state.revision) {
			this.sendOperation = { id: crypto.randomUUID(), revision: this.state.revision };
		}

		return this.sendOperation.id;
	}

	releaseSendIntent() {
		this.sendOperation = null;
	}

	renewAttachment(id: string) {
		if (this.state.audio?.id === id) {
			this.patch({ audio: { ...this.state.audio, id: crypto.randomUUID() } });
		} else {
			this.patch({
				images: this.state.images.map((image) =>
					image.id === id ? { ...image, id: crypto.randomUUID() } : image,
				),
			});
		}
	}

	clearSent(revision: number) {
		if (this.state.revision !== revision) {
			return;
		}

		this.patch({
			text: "",
			direction: "",
			suggestion: null,
			directed: false,
			revision: revision + 1,
		});
	}

	dispose() {
		this.active = false;

		for (const image of this.state.images) {
			URL.revokeObjectURL(image.url);
		}

		this.removeAudio();
		this.listeners.clear();
	}
}

const sessions = new Map<number, ConversationSession>();

export const Conversations = {
	list: { search: "", waiting: false, top: 0 },

	forPerson(personId: number) {
		let session = sessions.get(personId);

		if (!session) {
			session = new ConversationSession();
			sessions.set(personId, session);
		}

		return session;
	},

	clear() {
		for (const session of sessions.values()) {
			session.dispose();
		}

		sessions.clear();
		this.list = { search: "", waiting: false, top: 0 };
	},
};
